import {useEffect, useState} from 'react'
import {Header} from '../../componentsRef/Header'
import {BarChart3, ChevronDown, Download, Search} from 'lucide-react'
import settingCircle2 from '../../assets/img/background-catalog.svg'
import settingCircle1 from '../../assets/img/background-catalog2.svg'
import {
	CatalogTable,
	catalogSearchColumns,
	getCatalogSearchRowClassName,
} from '../../components/NewComponents/CatalogTable'
import {TarjetaPF} from '../../componentsRef/TarjetaPF'
import {CodigoPopNew} from '../../componentsRef/IngresoPromocion/CodigoPopNew'
import {getAllPromotions} from '../../services/promotion.service'
import '../../assets/css/setting.css'

const emptyFilters = {
	nombre_cadena: '',
	codigo_productos: '',
	fecha_inicio: '',
	fecha_termino: '',
	tipo_promocion: '',
	tipo_filtro_fechas: 'sellin',
	id_promociones: '',
	categoria: '',
	fase: '',
	estado_promocional: '',
}

const splitValues = (value) =>
	String(value || '')
		.split(',')
		.map((item) => item.trim())
		.filter(Boolean)

const optionValue = (option) => {
	if (option && typeof option === 'object') return option.value
	return option
}

const optionLabel = (option) => {
	if (option && typeof option === 'object') return option.label
	return option
}

export const CatalogSearch = () => {
	const [promotions, setPromotions] = useState([])
	const [filterOptions, setFilterOptions] = useState({})
	const [filters, setFilters] = useState(emptyFilters)
	const [loading, setLoading] = useState(false)
	const [errorMessage, setErrorMessage] = useState('')
	const [isPanelOpen, setIsPanelOpen] = useState(true)
	const [isResultsOpen, setIsResultsOpen] = useState(true)
	const [showGantt, setShowGantt] = useState(false)
	const [catalogSelected, setCatalogSelected] = useState(null)
	const [productSelected, setProductSelected] = useState(null)

	const buildPayload = () => ({
		nombre_cadenas: filters.nombre_cadena ? [filters.nombre_cadena] : [],
		codigo_productos: splitValues(filters.codigo_productos),
		fecha_inicio: filters.fecha_inicio || '',
		fecha_termino: filters.fecha_termino || '',
		tipo_promocion: filters.tipo_promocion || '',
		tipo_filtro_fechas: filters.tipo_filtro_fechas || 'sellin',
		id_promociones: splitValues(filters.id_promociones).map((id) => Number(id)).filter(Number.isFinite),
		categorias: filters.categoria ? [filters.categoria] : [],
		fases: filters.fase ? [filters.fase] : [],
		estados_promocionales: filters.estado_promocional ? [filters.estado_promocional] : [],
	})

	const normalizeResponse = (data) => {
		if (Array.isArray(data)) {
			return {promotions: data, filters: {}}
		}

		return {
			promotions: Array.isArray(data?.promotions) ? data.promotions : [],
			filters: data?.filters || {},
		}
	}

	const fetchPromotions = async () => {
		setLoading(true)
		setErrorMessage('')

		try {
			const {data} = await getAllPromotions(buildPayload())
			const normalizedData = normalizeResponse(data)
			setPromotions(normalizedData.promotions)

			if (normalizedData.filters && Object.keys(normalizedData.filters).length > 0) {
				setFilterOptions(normalizedData.filters)
			}
		} catch (error) {
			console.log('Error buscando promociones:', error)
			setErrorMessage('No fue posible obtener el historial promocional. Intenta nuevamente.')
			setPromotions([])
		} finally {
			setLoading(false)
		}
	}

	useEffect(() => {
		fetchPromotions()
	}, [])

	useEffect(() => {
    // 1. Guardar los estilos previos
    const previousBodyOverflow = document.body.style.overflow;
    const previousBodyOverflowY = document.body.style.overflowY;
    const previousBodyHeight = document.body.style.height;
    const previousBodyMinHeight = document.body.style.minHeight;

    const previousHtmlOverflow = document.documentElement.style.overflow;
    const previousHtmlOverflowY = document.documentElement.style.overflowY;
    const previousHtmlHeight = document.documentElement.style.height;

    // 2. Aplicar los nuevos estilos (¡con unidades!)
    document.documentElement.style.height = '100%'; 
    document.documentElement.style.overflow = 'auto';
    document.documentElement.style.overflowY = 'auto';

    document.body.style.overflow = 'auto';
    document.body.style.overflowY = 'auto';
    
    // Usar minHeight: 100vh asegura que ocupe TODA la pantalla, 
    // pero si hay más contenido, crecerá de forma natural.
    document.body.style.minHeight = '100vh'; 
    document.body.style.height = 'auto'; 

    // 3. Limpiar los estilos cuando el componente se desmonte
    return () => {
        document.body.style.overflow = previousBodyOverflow;
        document.body.style.overflowY = previousBodyOverflowY;
        document.body.style.height = previousBodyHeight;
        document.body.style.minHeight = previousBodyMinHeight;

        document.documentElement.style.overflow = previousHtmlOverflow;
        document.documentElement.style.overflowY = previousHtmlOverflowY;
        document.documentElement.style.height = previousHtmlHeight;
    };
}, []);

	const handleChange = (field) => (event) => {
		setFilters((prevFilters) => ({
			...prevFilters,
			[field]: event.target.value,
		}))
	}

	const resetFilters = () => {
		setFilters(emptyFilters)
	}

	const exportTableToExcel = () => {
		if (!promotions.length) {
			alert('No hay resultados para exportar.')
			return
		}

		const table = document.getElementById('catalog-search-table')
		if (!table) return

		const html = `
			<html>
				<head><meta charset="UTF-8" /></head>
				<body>${table.outerHTML}</body>
			</html>
		`
		const blob = new Blob([html], {type: 'application/vnd.ms-excel;charset=utf-8;'})
		const url = window.URL.createObjectURL(blob)
		const link = document.createElement('a')
		link.href = url
		link.download = `historial-promocional-${new Date().toISOString().slice(0, 10)}.xls`
		document.body.appendChild(link)
		link.click()
		link.remove()
		window.URL.revokeObjectURL(url)
	}

	const parseDate = (value) => {
		const [day, month, year] = String(value || '').split('/').map(Number)
		if (!day || !month || !year) return null
		const date = new Date(year, month - 1, day)
		return Number.isNaN(date.getTime()) ? null : date
	}

	const ganttRows = promotions
		.map((promotion) => {
			const isSellOut = String(promotion.tipo_promocion).toUpperCase() === 'SELL-OUT'
			const start = parseDate(isSellOut ? promotion.inicio_sellout : promotion.inicio_sellin)
			const end = parseDate(isSellOut ? promotion.termino_sellout : promotion.termino_sellin)

			if (!start || !end) return null

			return {
				...promotion,
				start,
				end,
				label: `${promotion.nombre_promocion || '-'} | ${promotion.codigo_producto || '-'} | ${promotion.nombre_cadena || '-'} | ${promotion.tipo_promocion || '-'}`,
			}
		})
		.filter(Boolean)

	const ganttMinDate = ganttRows.length
		? new Date(Math.min(...ganttRows.map((row) => row.start.getTime())))
		: null
	const ganttMaxDate = ganttRows.length
		? new Date(Math.max(...ganttRows.map((row) => row.end.getTime())))
		: null
	const ganttTotalDays =
		ganttMinDate && ganttMaxDate
			? Math.max(1, Math.ceil((ganttMaxDate - ganttMinDate) / 86400000) + 1)
			: 1

	const getGanttBarStyle = (row) => {
		if (!ganttMinDate) return {}
		const offsetDays = Math.max(0, Math.floor((row.start - ganttMinDate) / 86400000))
		const durationDays = Math.max(1, Math.ceil((row.end - row.start) / 86400000) + 1)

		return {
			left: `${(offsetDays / ganttTotalDays) * 100}%`,
			width: `${Math.max(2, (durationDays / ganttTotalDays) * 100)}%`,
		}
	}

	const formatGanttDate = (date) => {
		if (!date) return '-'
		return date.toLocaleDateString('es-CL')
	}

	const ganttAxisTicks = (() => {
		if (!ganttMinDate || !ganttMaxDate) return []
		const tickCount = Math.min(6, Math.max(2, ganttTotalDays))
		return Array.from({length: tickCount}, (_, index) => {
			const ratio = tickCount === 1 ? 0 : index / (tickCount - 1)
			const dayOffset = Math.round((ganttTotalDays - 1) * ratio)
			const date = new Date(ganttMinDate)
			date.setDate(ganttMinDate.getDate() + dayOffset)
			return {
				date,
				left: `${ratio * 100}%`,
			}
		})
	})()

	const renderSelect = (label, field, options = []) => (
		<label className='catalog-search-filter'>
			<span>{label}</span>
			<select value={filters[field]} onChange={handleChange(field)}>
				<option value=''>Todos</option>
				{options.map((option) => (
					<option key={`${field}-${optionValue(option)}`} value={optionValue(option)}>
						{optionLabel(option)}
					</option>
				))}
			</select>
		</label>
	)

	const openCatalogCard = (promotion) => {
		setCatalogSelected({
			...promotion,
			id_promo: promotion.id_promocion,
			nombre_promocion: promotion.nombre_promocion,
			fase_promocion: promotion.nombre_fase,
			estado_fase: promotion.nombre_estado_promocional,
			estado_promocional: promotion.id_estado_promocional || promotion.id_fase || 1,
			sku_count: promotion.sku_count || promotion.n_sku || promotion.cantidad_sku || '-',
			uplift: promotion.uplift ?? promotion.uplift_promocional ?? '-',
			baseline: promotion.baseline ?? promotion.baseline_kgs ?? '-',
			margen_incremental: promotion.margen_incremental ?? promotion.mg_promocional ?? '-',
			margen_comercial_incremental_porcentual:
				promotion.margen_comercial_incremental_porcentual ??
				promotion.roi ??
				'-',
		})
	}

	const openProductCard = (promotion) => {
		setProductSelected({
			idPromocion: promotion.id_promocion,
			dataProduct: {
				...promotion,
				codigo_producto: promotion.codigo_producto || '',
				descripcion: promotion.descripcion || promotion.nombre_producto || '',
			},
			promotion: {
				data_promocion: {
					id: promotion.id_promocion,
					id_promocion: promotion.id_promocion,
					nombre_promocion: promotion.nombre_promocion,
					nombre_cadena: promotion.nombre_cadena || '',
					tipo_promocion: promotion.tipo_promocion,
				},
			},
		})
	}

	return (
		<div className='CatalogSearch'>
			<img src={settingCircle1} className='circle-1-setting' alt='' />
			<img src={settingCircle2} className='circle-2-setting' alt='' />

			<Header />


			<div className={`catalog-search-panel ${!isPanelOpen ? 'collapsed' : ''}`}>
				<div className='catalog-search-panel-header'>
					<div>
						<Search size={18} color="#3676F5" />
						<h2>Filtros de búsqueda</h2>
					</div>
					<button
						type='button'
						className='catalog-search-toggle-button'
						onClick={() => setIsPanelOpen((prevState) => !prevState)}
						aria-label={isPanelOpen ? 'Ocultar filtros' : 'Mostrar filtros'}
					>
						<ChevronDown size={18} className={!isPanelOpen ? 'collapsed' : ''} />
					</button>
				</div>

				{isPanelOpen && (
					<>
						<div className='catalog-search-filters'>
							<label className='catalog-search-filter'>
								<span>Códigos producto</span>
								<input
									type='text'
									value={filters.codigo_productos}
									onChange={handleChange('codigo_productos')}
									placeholder='Ej: 1029,1030'
								/>
							</label>

							<label className='catalog-search-filter'>
								<span>ID promociones</span>
								<input
									type='text'
									value={filters.id_promociones}
									onChange={handleChange('id_promociones')}
									placeholder='Ej: 6059,6067'
								/>
							</label>

							<label className='catalog-search-filter'>
								<span>Fecha inicio</span>
								<input type='date' value={filters.fecha_inicio} onChange={handleChange('fecha_inicio')} />
							</label>

							<label className='catalog-search-filter'>
								<span>Fecha término</span>
								<input type='date' value={filters.fecha_termino} onChange={handleChange('fecha_termino')} />
							</label>

							{renderSelect('Cadena', 'nombre_cadena', filterOptions.nombre_cadenas || [])}
							{renderSelect('Categoría', 'categoria', filterOptions.categorias || [])}
							{renderSelect('Fase', 'fase', filterOptions.fases || [])}
							{renderSelect('Estado promocional', 'estado_promocional', filterOptions.estados_promocionales || [])}
							{renderSelect('Tipo promoción', 'tipo_promocion', filterOptions.tipo_promocion || ['sellin', 'sellout'])}
							{renderSelect('Filtro fechas', 'tipo_filtro_fechas', filterOptions.tipo_filtro_fechas || ['sellin', 'sellout'])}
						</div>

						<div className='catalog-search-actions'>
							<button type='button' className='catalog-search-secondary-button' onClick={resetFilters} disabled={loading}>
								Limpiar filtros
							</button>
							<button type='button' className='catalog-search-secondary-button' onClick={() => setShowGantt((prevState) => !prevState)} disabled={loading || !promotions.length}>
								<BarChart3 size={16} />
								{showGantt ? 'Ocultar Gantt' : 'Generar Gantt'}
							</button>
							<button type='button' className='catalog-search-secondary-button' onClick={exportTableToExcel} disabled={loading || !promotions.length}>
								<Download size={16} />
								Exportar Excel
							</button>
							<button type='button' className='catalog-search-primary-button' onClick={fetchPromotions} disabled={loading}>
								<Search size={16} />
								{loading ? 'Buscando...' : 'Buscar'}
							</button>
						</div>
					</>
				)}
			</div>

			{showGantt && (
				<div className='catalog-search-gantt'>
					<div className='catalog-search-gantt-header'>
						<h2>Gantt promocional</h2>
						<p>{formatGanttDate(ganttMinDate)} - {formatGanttDate(ganttMaxDate)}</p>
					</div>
					{ganttRows.length ? (
						<>
							<div className='catalog-search-gantt-body'>
								{ganttRows.map((row, index) => (
									<div
										className='catalog-search-gantt-row'
										key={`${row.id_promocion}-${row.codigo_producto}-${index}`}
									>
										<div className='catalog-search-gantt-label'>
											<strong>{row.nombre_promocion || '-'}</strong>
											<span>{row.codigo_producto || '-'} | {row.tipo_promocion || '-'} | {row.nombre_cadena || '-'} | ID {row.id_promocion || '-'}</span>
										</div>
										<div className='catalog-search-gantt-track'>
											<div className={`catalog-search-gantt-bar ${getCatalogSearchRowClassName(row)}`} style={getGanttBarStyle(row)}>
												<span>{formatGanttDate(row.start)} - {formatGanttDate(row.end)}</span>
											</div>
										</div>
									</div>
								))}
							</div>
							<div className='catalog-search-gantt-axis-row'>
								<div className='catalog-search-gantt-axis-spacer'></div>
								<div className='catalog-search-gantt-axis'>
									{ganttAxisTicks.map((tick, index) => (
										<div className='catalog-search-gantt-tick' style={{left: tick.left}} key={`gantt-axis-${index}`}>
											<span></span>
											<p>{formatGanttDate(tick.date)}</p>
										</div>
									))}
								</div>
							</div>
						</>
					) : (
						<p className='catalog-search-gantt-empty'>No hay fechas válidas para construir el Gantt.</p>
					)}
				</div>
			)}

			<div className={`search-table-area catalog-search-results-panel ${!isResultsOpen ? 'collapsed' : ''}`}>
				<div className='catalog-search-panel-header'>
					<div>
						<Search size={18} color="#3676F5" />
						<h2>{`${promotions.length} Resultados`}</h2>
					</div>
					<button
						type='button'
						className='catalog-search-toggle-button'
						onClick={() => setIsResultsOpen((prevState) => !prevState)}
						aria-label={isResultsOpen ? 'Ocultar resultados' : 'Mostrar resultados'}
					>
						<ChevronDown size={18} className={!isResultsOpen ? 'collapsed' : ''} />
					</button>
				</div>
				{isResultsOpen && (
					<>
						{errorMessage && <p className='catalog-search-error'>{errorMessage}</p>}
						<CatalogTable
							data={promotions}
							columns={catalogSearchColumns}
							onPromotionClick={openCatalogCard}
							onProductClick={openProductCard}
						/>
					</>
				)}
			</div>

			{catalogSelected && (
				<TarjetaPF
					catalogSelected={catalogSelected}
					setActive={setCatalogSelected}
					Activityactive={null}
				/>
			)}

			{productSelected && (
				<CodigoPopNew
					dataProduct={productSelected.dataProduct}
					idPromocion={productSelected.idPromocion}
					promotion={productSelected.promotion}
					setCodigoPop={() => setProductSelected(null)}
					saveCatalogBeforeComment={async () => {}}
				/>
			)}
		</div>
	)
}
